import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "../../../../components/tables/utils/tableUtils.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import AssignObjectToContractDialog from "./AssignObjectToContractDialog.jsx";
import ObjectAssignmentChoiceDialog from "./ObjectAssignmentChoiceDialog.jsx";
import ObjektImportPreviewDialog from "../../../contracts/components/ObjektImportPreviewDialog.jsx";

/**
 * Reine Leseliste der Verträge eines Kunden (exakter Server-Filter über
 * kunde_id_ov, siehe KundeController::customerContracts()). Filter-Tabs
 * Aktive/Inaktive/Alle basieren auf beginn/ablauf (Vertrag::scopeActivity()).
 * Je Vertrag per Accordion aufklappbar: Tabelle der zugehörigen Objekte
 * (analog zum Sparten-Detail-Panel in CustomerObjectsTable.jsx).
 */
export default function CustomerContractsTable({ customerId, objectRefNumLabel }) {
  const { enqueueSnackbar } = useSnackbar();
  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [choiceDialogVertrag, setChoiceDialogVertrag] = useState(null);
  const [assignDialogVertrag, setAssignDialogVertrag] = useState(null);
  const [importDialogVertrag, setImportDialogVertrag] = useState(null);

  // Deep-link von der Objekte-Tabelle: Klick auf eine Vertragsnummer dort
  // springt hierher und filtert direkt auf diesen Vertrag (siehe
  // CustomerObjectsTable.jsx). Wird als echter MRT-Spaltenfilter geseeded
  // (nicht als stilles extraParams.filters), damit der Filter in der Tabelle
  // sichtbar aktiv ist - siehe useMultiTableData.jsx: table.filters seedet
  // tableState.filters, das dieselbe {id, value}[]-Form wie MRTs
  // columnFilters hat.
  const [searchParams, setSearchParams] = useSearchParams();
  const vertragsnummerFilter = searchParams.get("vertragsnummer") || "";
  // Nur als Initialwert für den Mount relevant (TabPanel unmounted inaktive
  // Tabs beim Tab-Wechsel, siehe CustomerShowPage.jsx, also frischer Mount
  // pro Deep-Link) - ein Ändern des Filters direkt über die Tabellen-UI
  // läuft danach über useMultiTableData/DataTable selbst weiter.
  const initialColumnFilters = vertragsnummerFilter
    ? [{ id: "vertragsnummer", value: vertragsnummerFilter }]
    : [];

  const handleChoiceContinue = (method) => {
    const vertrag = choiceDialogVertrag;
    setChoiceDialogVertrag(null);
    if (method === "import") {
      setImportDialogVertrag(vertrag);
    } else {
      setAssignDialogVertrag(vertrag);
    }
  };

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const tableKey = "customerContracts";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Verträge",
        route: `/data/kundes/${customerId}/contracts`,
        pageSize: 10,
        storageKey: "v2_table_customer_contracts",
        filters: initialColumnFilters,
        extraParams: {
          include: ["gesellschaft", "sachbearbeiter", "objekts", "sparten"],
          filters: { activity: activeFilterTab },
        },
      },
    ],
    // initialColumnFilters absichtlich nicht als Dependency: siehe Kommentar
    // bei dessen Definition - es ist nur der einmalige Mount-Initialwert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, activeFilterTab]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Neu laden, wenn zwischen Aktiv/Inaktiv/Alle gewechselt wird
  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterTab]);

  const filterTabs = useMemo(
    () => [
      { key: "all", label: "Alle", onClick: setActiveFilterTab },
      { key: "active", label: "Aktive", onClick: setActiveFilterTab },
      { key: "inactive", label: "Inaktive", onClick: setActiveFilterTab },
    ],
    []
  );

  const columns = useMemo(
    () => [
      { accessorKey: "vertragsnummer", header: "Vertragsnummer", size: 150 },
      { accessorKey: "vertragsart", header: "Vertragsart", size: 150 },
      {
        // AllowedFilter::partial('gesellschaft.name') existiert bereits im
        // Backend - Filter bleibt aktiv (id explizit gesetzt, siehe Kommentar
        // bei anderen Punkt-Spalten in diesem Projekt).
        accessorKey: "gesellschaft.name",
        id: "gesellschaft.name",
        header: "Gesellschaft",
        size: 180,
        Cell: ({ row }) => row.original.gesellschaft?.name || "—",
      },
      {
        // Kein Backend-Filter/-Sort (bräuchte einen Join auf users) - nicht "schnell" machbar.
        accessorKey: "sachbearbeiter",
        header: "Sachbearbeiter",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const sachbearbeiter = row.original.sachbearbeiter;
          // UserResource liefert full_name (nicht vorname/nachname), siehe
          // BE/app/Domain/User/UserResource.php.
          return sachbearbeiter?.full_name?.trim() || "—";
        },
      },
      {
        accessorKey: "beginn",
        header: "Beginn",
        size: 120,
        Cell: ({ row }) => getDatum(row.original.beginn),
      },
      {
        accessorKey: "ablauf",
        header: "Ablauf",
        size: 120,
        Cell: ({ row }) => getDatum(row.original.ablauf),
      },
      {
        accessorKey: "actions",
        id: "actions",
        header: "",
        size: 20,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const vertrag = row.original;

          return (
            <>
              <IconButton
                size="small"
                aria-label="Weitere Aktionen"
                onClick={(e) => handleMenuOpen(e, vertrag.id)}
              >
                <MoreVertIcon fontSize="inherit" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && menuRowId === vertrag.id}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    setChoiceDialogVertrag(vertrag);
                  }}
                >
                  <MapsHomeWorkOutlinedIcon style={{ marginRight: "8px" }} />
                  Objekte zuordnen
                </MenuItem>
              </Menu>
            </>
          );
        },
      },
    ],
    [anchorEl, menuRowId]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        filterTabs={filterTabs}
        activeFilterTab={activeFilterTab}
        initialState={
          initialColumnFilters.length > 0
            ? { filters: initialColumnFilters, showColumnFilters: true }
            : undefined
        }
        enableRowExpansion
        enablePagination
        manualPagination
        pageIndex={tableState.pageIndex ?? 0}
        pageSize={tableState.pageSize ?? 10}
        totalRows={tableState.totalRows ?? rows.length}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? 10,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) return;
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeSorting={(sorting) =>
          updateTableState(tableKey, {
            sorting: applySortingWithAccessorKeys(sorting, columns),
          })
        }
        onChangeFilters={(filters) => {
          updateTableState(tableKey, { filters });
          // Deep-Link-Param in der URL synchron halten, falls der Filter
          // direkt über die Tabellen-Filterzeile geändert oder entfernt wird.
          if (vertragsnummerFilter) {
            const entry = filters.find((f) => f.id === "vertragsnummer");
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              if (entry?.value) {
                next.set("vertragsnummer", entry.value);
              } else {
                next.delete("vertragsnummer");
              }
              return next;
            });
          }
        }}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey="v2_table_customer_contracts"
        renderDetailPanel={({ row }) => (
          <Box sx={{ p: 2 }}>
            {Array.isArray(row.original.objekts) &&
            row.original.objekts.length > 0 ? (
              <DataTable
                columns={[
                  {
                    accessorKey: "bezeichnung",
                    header: "Bezeichnung",
                    size: 200,
                  },
                  { accessorKey: "strasse", header: "Straße", size: 150 },
                  { accessorKey: "hausnr", header: "Hausnummer", size: 100 },
                  { accessorKey: "plz", header: "PLZ", size: 80 },
                  { accessorKey: "ort", header: "Ort", size: 150 },
                  ...(objectRefNumLabel
                    ? [
                        {
                          accessorKey: "customer_reference_number",
                          header: objectRefNumLabel,
                          size: 180,
                        },
                      ]
                    : []),
                ]}
                data={row.original.objekts}
                pageSize={99999}
                enableFooter={false}
                enableTopToolbar={false}
                enablePagination={false}
                enableColumnFilters={false}
                enableSorting={true}
                enableBottomToolbar={false}
                enableGlobalFilter={false}
                enableRowExpansion
                renderDetailPanel={({ row: objektRow }) => (
                  <Box sx={{ p: 2 }}>
                    {Array.isArray(objektRow.original.sparten) &&
                    objektRow.original.sparten.length > 0 ? (
                      <DataTable
                        columns={[
                          {
                            accessorKey: "sparte",
                            header: "Sparte",
                            size: 150,
                          },
                          {
                            accessorKey: "zugang",
                            header: "Zugang",
                            size: 110,
                            Cell: ({ row: sparteRow }) =>
                              getDatum(sparteRow.original.zugang),
                          },
                          {
                            accessorKey: "abgang",
                            header: "Abgang",
                            size: 110,
                            Cell: ({ row: sparteRow }) =>
                              getDatum(sparteRow.original.abgang),
                          },
                          {
                            accessorKey: "jnetto",
                            header: "Jahresnetto",
                            size: 130,
                          },
                          {
                            accessorKey: "jbrutto",
                            header: "Jahresbrutto",
                            size: 130,
                          },
                          {
                            accessorKey: "sb_fix",
                            header: "SB fix",
                            size: 100,
                          },
                          {
                            accessorKey: "sb_min",
                            header: "SB min",
                            size: 100,
                          },
                          {
                            accessorKey: "sb_max",
                            header: "SB max",
                            size: 100,
                          },
                          {
                            accessorKey: "sb_prozent",
                            header: "SB %",
                            size: 90,
                          },
                        ]}
                        data={objektRow.original.sparten}
                        pageSize={99999}
                        enableFooter={false}
                        enableTopToolbar={false}
                        enablePagination={false}
                        enableColumnFilters={false}
                        enableSorting={true}
                        enableBottomToolbar={false}
                        enableGlobalFilter={false}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ pl: 2 }}>
                        Keine Objektsparten verknüpft
                      </Typography>
                    )}
                  </Box>
                )}
              />
            ) : (
              <Typography variant="body2" sx={{ pl: 2 }}>
                Keine Objekte verknüpft
              </Typography>
            )}
          </Box>
        )}
      />
      <ObjectAssignmentChoiceDialog
        open={!!choiceDialogVertrag}
        onClose={() => setChoiceDialogVertrag(null)}
        onContinue={handleChoiceContinue}
      />
      <AssignObjectToContractDialog
        open={!!assignDialogVertrag}
        customerId={customerId}
        vertrag={assignDialogVertrag}
        onClose={() => setAssignDialogVertrag(null)}
        onSuccess={() => {
          setAssignDialogVertrag(null);
          fetchData(tableKey);
        }}
        objectRefNumLabel={objectRefNumLabel}
      />
      <ObjektImportPreviewDialog
        open={!!importDialogVertrag}
        vertrag={
          importDialogVertrag && {
            id: importDialogVertrag.id,
            id_ov: importDialogVertrag.id_ov,
            label:
              importDialogVertrag.gesellschaft?.name +
              " | VSNR: " +
              importDialogVertrag.vertragsnummer,
          }
        }
        onClose={() => setImportDialogVertrag(null)}
        onSuccess={() => {
          setImportDialogVertrag(null);
          fetchData(tableKey);
        }}
        objectRefNumLabel={objectRefNumLabel}
      />
    </>
  );
}

CustomerContractsTable.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  objectRefNumLabel: PropTypes.string,
};
